import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    Typography,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    InputAdornment,
    Divider,
    CircularProgress,
    Box
} from '@mui/material';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import SensorsIcon from '@mui/icons-material/Sensors';
import SensorsOffIcon from '@mui/icons-material/SensorsOff';
import Cookies from 'js-cookie';
import api from '../../../../../services/api';

export default function SinalizarStatus({ open, onClose, onStatusChange, darkMode }) {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [veiculos, setVeiculos] = useState([]);
    const [veiculoSelecionado, setVeiculoSelecionado] = useState('');
    const [lat, setLat] = useState('');
    const [lng, setLng] = useState('');
    const [usuario, setUsuario] = useState(null);
    const [isOnline, setIsOnline] = useState(false);

    useEffect(() => {
        if (open) {
            fetchInitialData();
        }
    }, [open]);

    async function fetchInitialData() {
        setLoading(true);
        try {
            const token = Cookies.get('token');
            const idUsuario = Cookies.get('id_usuario');
            const headers = { 'Authorization': 'Bearer ' + token };

            // Fetch User Current Status
            const resUser = await api.get(`/v1/protected/user/retornardadosusuario/${idUsuario}`, { headers });
            const uData = resUser.data;
            setUsuario(uData);
            setLat(uData.latitude || '');
            setLng(uData.longitude || '');
            setVeiculoSelecionado(uData.id_veiculo_ronda_motorizada || '');
            setIsOnline(uData.status_ronda_motorizada_online === 1);

            // Fetch Vehicles
            const resVeic = await api.get('/v1/protected/veiculos/listartodos', { headers });
            
            // Handle different pagination structures safely
            const vehicleList = resVeic.data?.dados || (Array.isArray(resVeic.data) ? resVeic.data : []);
            setVeiculos(vehicleList);

        } catch (err) {
            console.error('Erro ao buscar dados iniciais:', err);
        } finally {
            setLoading(false);
        }
    }

    const handleGetLocation = () => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    setLat(position.coords.latitude.toFixed(6));
                    setLng(position.coords.longitude.toFixed(6));
                },
                (error) => {
                    alert('Erro ao obter localização: ' + error.message);
                }
            );
        } else {
            alert('Geolocalização não é suportada por este navegador.');
        }
    };

    const handleSaveStatus = async (status) => {
        if (status === 1 && (!veiculoSelecionado || !lat || !lng)) {
            alert('Por favor, selecione um veículo e informe sua localização.');
            return;
        }

        if (!usuario) {
            alert('Dados do usuário não carregarados. Tente novamente em instantes.');
            return;
        }

        setSaving(true);
        try {
            const token = Cookies.get('token');
            const headers = { 'Authorization': 'Bearer ' + token };
            
            // Prepare update object - Mirroring atualizarconfiguracoes pattern
            const updateData = {
                ...usuario,
                status_ronda_motorizada_online: status,
                latitude: lat,
                longitude: lng,
                id_veiculo_ronda_motorizada: veiculoSelecionado
            };

            const idUsuario = Cookies.get('id_usuario');
            await api.put(`/v1/protected/user/atualizarconfiguracoes/${idUsuario}`, updateData, { headers });
            
            setIsOnline(status === 1);
            alert(status === 1 ? 'Você está ONLINE!' : 'Você está agora OFFLINE.');
            if (onStatusChange) onStatusChange(status === 1);
            onClose();
        } catch (err) {
            console.error('Erro ao atualizar status:', err);
            alert('Erro ao atualizar status. Tente novamente.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog 
            open={open} 
            onClose={onClose} 
            maxWidth="sm" 
            fullWidth
            PaperProps={{
                sx: {
                    bgcolor: darkMode ? '#1e1e1e' : '#fff',
                    color: darkMode ? '#fff' : 'inherit',
                    borderRadius: 3
                }
            }}
        >
            <DialogTitle>
                <Box display="flex" alignItems="center">
                    <DirectionsCarIcon sx={{ mr: 1 }} color="primary" />
                    Sinalizar Status de Ronda Motorizada
                </Box>
            </DialogTitle>
            <DialogContent dividers sx={{ borderColor: darkMode ? '#333' : '#eee' }}>
                {loading ? (
                    <Box display="flex" justifyContent="center" p={3}><CircularProgress /></Box>
                ) : (
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" color="textSecondary">
                                Sua situação atual: <strong>{isOnline ? 'ONLINE 🟢' : 'OFFLINE 🔴'}</strong>
                            </Typography>
                        </Grid>
                        
                        <Grid item xs={12}>
                            <FormControl fullWidth>
                                <InputLabel>Veículo em Uso</InputLabel>
                                <Select
                                    value={veiculoSelecionado}
                                    onChange={(e) => setVeiculoSelecionado(e.target.value)}
                                    label="Veículo em Uso"
                                    startAdornment={<InputAdornment position="start"><DirectionsCarIcon /></InputAdornment>}
                                >
                                    {Array.isArray(veiculos) && veiculos.map(v => (
                                        <MenuItem key={v.id_veiculo} value={v.id_veiculo}>
                                            {v.marca} {v.modelo} - {v.placa}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Latitude"
                                fullWidth
                                value={lat}
                                onChange={(e) => setLat(e.target.value)}
                                InputProps={{
                                    startAdornment: <InputAdornment position="start"><MyLocationIcon /></InputAdornment>
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Longitude"
                                fullWidth
                                value={lng}
                                onChange={(e) => setLng(e.target.value)}
                                InputProps={{
                                    startAdornment: <InputAdornment position="start"><MyLocationIcon /></InputAdornment>
                                }}
                            />
                        </Grid>
                        
                        <Grid item xs={12}>
                            <Button 
                                variant="outlined" 
                                startIcon={<MyLocationIcon />} 
                                onClick={handleGetLocation}
                                fullWidth
                            >
                                Obter Localização Atual (GPS)
                            </Button>
                        </Grid>
                    </Grid>
                )}
            </DialogContent>
            <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
                <Button onClick={onClose} color="inherit">Cancelar</Button>
                <Box>
                    {isOnline ? (
                        <Button 
                            variant="contained" 
                            color="error" 
                            startIcon={<SensorsOffIcon />}
                            onClick={() => handleSaveStatus(0)}
                            disabled={saving}
                        >
                            Ficar OFFLINE
                        </Button>
                    ) : (
                        <Button 
                            variant="contained" 
                            color="success" 
                            startIcon={<SensorsIcon />}
                            onClick={() => handleSaveStatus(1)}
                            disabled={saving || loading}
                        >
                            Ficar ONLINE
                        </Button>
                    )}
                </Box>
            </DialogActions>
        </Dialog>
    );
}

